import { actionRenewSyncToken } from "@/actions/renewSyncToken.action"
import type { SyncClient, SyncDocument, SyncMap } from "twilio-sync"
import { syncStore$ } from "@/stores/sync.v2.store"
import consola from "consola"
import { batch } from "@legendapp/state"
import type { TRingioSyncDocument, TRingioSyncMap } from "./syncTypes"
import { inflateMapItem, loadSync } from "./helpers"
import { processAndStoreContactData } from "./handlers/maps/contacts.handler"
import type { TSyncBootstrapData } from "@/lib/sync-bootstrap"

//////////////////////////////////////////////////////////////////////////////
//////////////////////////////////////////////////////////////////////////////
/**
 * About this file:
 *
 * Currently this only supports documents and not maps (although we can support them).
 * Currently it only supports the following document types:
 * - account
 * - userStatus
 * - userPreferences
 * - cluster
 * The rest of the document types should be linked to the corresponding schema and the corresponding state.
 * Also they would need to be enabled in the SYNC_CONNECTIONS object.
 * To avoid reinitializing the sync connection, about switching pages using window.location methods, only
 * use the router methods from Next.js (next/navigation package).
 *
 * To finish the sync implementation, check how I did it in the mobile app at https://github.com/ringio/ringio-mobile-app/blob/update/src/services/SyncService.ts
 *
 * The sync states are preinitialized with the user data from the login method obtained in the first server call, so if it always safe to cast the states to their corresponding schemas, they will always be valid.
 */
//////////////////////////////////////////////////////////////////////////////
//////////////////////////////////////////////////////////////////////////////

interface IInitializeTwilioSyncClientProps {
  token: string
  initialUserData: TSyncBootstrapData
}

interface SyncBootstrapState {
  userStatus?: TSyncBootstrapData["user"]
  account?: TSyncBootstrapData["account"]
  cluster?: TSyncBootstrapData["cluster"]
  userPreferences?: TSyncBootstrapData["preferences"]
}

function isSameCluster(currentCluster: TSyncBootstrapData["cluster"], nextCluster: TSyncBootstrapData["cluster"]) {
  return (
    currentCluster.adminUrlS === nextCluster.adminUrlS &&
    currentCluster.apiUrlS === nextCluster.apiUrlS &&
    currentCluster.chromeExtensionWebstoreId === nextCluster.chromeExtensionWebstoreId &&
    currentCluster.launcherUrlS === nextCluster.launcherUrlS &&
    currentCluster.remoteUrlS === nextCluster.remoteUrlS &&
    currentCluster.voiceUrlS === nextCluster.voiceUrlS
  )
}

export function shouldHydrateSyncStoreFromInitialUserData({ currentState, initialUserData }: { currentState: SyncBootstrapState; initialUserData: TSyncBootstrapData }) {
  if (!currentState.userStatus || !currentState.account || !currentState.cluster || !currentState.userPreferences) {
    return true
  }

  if (currentState.userStatus.id !== initialUserData.user.id) {
    return true
  }

  if (currentState.userStatus.accountTrialType !== initialUserData.user.accountTrialType) {
    return true
  }

  if (currentState.account.accountSid !== initialUserData.account.accountSid) {
    return true
  }

  if (currentState.account.isSoloFree !== initialUserData.account.isSoloFree) {
    return true
  }

  if (currentState.account.clusterId !== initialUserData.account.clusterId) {
    return true
  }

  if (!isSameCluster(currentState.cluster, initialUserData.cluster)) {
    return true
  }

  return false
}

export function hydrateSyncStoreFromInitialUserData(initialUserData: TSyncBootstrapData) {
  if (
    !shouldHydrateSyncStoreFromInitialUserData({
      currentState: {
        userStatus: syncStore$.userStatus.get(),
        account: syncStore$.account.get(),
        cluster: syncStore$.cluster.get(),
        userPreferences: syncStore$.userPreferences.get(),
      },
      initialUserData,
    })
  ) {
    return
  }

  batch(() => {
    syncStore$.userStatus.set(initialUserData.user)
    syncStore$.account.set(initialUserData.account)
    syncStore$.cluster.set(initialUserData.cluster)
    syncStore$.userPreferences.set(initialUserData.preferences)
  })
}

export async function initializeTwilioSyncClient({ token, initialUserData }: IInitializeTwilioSyncClientProps) {
  consola.debug("Initializing sync client")

  const syncStatus = window.ringioFrontV3.twilio.sync.client?.connectionState

  if (syncStatus !== undefined && syncStatus !== "error" && syncStatus !== "disconnected" && syncStatus !== "unknown" && window.ringioFrontV3.twilio.sync.client) {
    consola.warn("Sync client already initialized")
    return
  }

  if (!token) {
    consola.error("Token is empty")
    return
  }

  const items: Record<string, string> = initialUserData.twilio.ids

  const { SyncClient } = await import("twilio-sync")
  const syncClient = new SyncClient(token, {
    logLevel: process.env.NEXT_PUBLIC_ENV === "production" ? "error" : "warn",
  })

  window.ringioFrontV3.twilio.sync.client = syncClient

  syncClient.on("connectionError", (error) => {
    console.warn("Connection error", error)

    syncStore$.status.set("error")
  })

  syncClient.on("tokenAboutToExpire", async () => {
    consola.warn("Token about to expire")
    await handlesyncTokenRenewal(syncClient)
  })

  syncClient.on("tokenExpired", async () => {
    consola.warn("Token expired")
    await handlesyncTokenRenewal(syncClient)
  })

  syncClient.on("connectionStateChanged", (state) => {
    consola.debug("Connection state changed", state)

    syncStore$.status.set(state)

    if (state === "connected") {
      for (const [key, value] of Object.entries(items)) {
        // No need to await here, we can load the documents in parallel
        loadSync({
          key,
          name: value,
        })
      }
    }
  })

  hydrateSyncStoreFromInitialUserData(initialUserData)
}

export async function updateSyncDocument({ data, document }: { document: TRingioSyncDocument; data: Record<string, unknown> }) {
  if (typeof window === "undefined") {
    consola.error("We can not update a sync document on the server")
    return false
  }

  if (!window.ringioFrontV3.twilio.sync.client) {
    consola.error("Sync client is not ready!")
    return false
  }

  const syncClient = window.ringioFrontV3.twilio.sync.client!

  if (syncClient.connectionState !== "connected") {
    consola.error("Sync client is not connected!")
    return false
  }

  const selectedDocument: SyncDocument | undefined = window.ringioFrontV3.twilio.sync.initialization.documents[document] as SyncDocument | undefined

  if (!selectedDocument) {
    consola.error(`Document ${document} is not loaded!`)
    return false
  }

  try {
    consola.debug(`Updating document ${document}`, data)
    await selectedDocument.update(data)
    return true
  } catch (err) {
    consola.error(`Error updating document ${document}`, err)
    return false
  }
}

/**
 * Handles the renewal of the sync token for the given SyncClient.
 * @param syncClient - The SyncClient instance.
 * @returns A Promise that resolves when the sync token is successfully renewed.
 */
async function handlesyncTokenRenewal(syncClient: SyncClient) {
  try {
    const newSyncToken = await actionRenewSyncToken()
    syncClient.updateToken(newSyncToken)
  } catch (err) {
    consola.error("Error updating the token", err)

    syncStore$.status.set("error")
    // TODO: Maybe retry the token update?
  }
}

/**
 * Fetches data for a specific key from a given map using the Twilio Sync client.
 *
 * @param {Object} params - The parameters for fetching the map key data.
 * @param {TRingioSyncMap} params.map - The map from which to fetch the key data.
 * @param {string} params.key - The key for which to fetch the data.
 * @param {Function} [params.onSuccess] - Optional callback function to be called with the fetched data on success.
 * @param {Function} [params.onError] - Optional callback function to be called with the error on failure.
 *
 * @returns {Promise<void>} - A promise that resolves when the operation is complete.
 *
 * @throws {Error} - Throws an error if the operation fails.
 *
 * @remarks
 * This function should only be called in a browser environment. It will log an error and return if called on the server.
 * It also checks if the Sync client is ready and connected before attempting to fetch the data.
 * If the map or key is not found, it logs an error and returns.
 */
export async function fetchMapKeyData({ key, map, onError, onSuccess }: { map: TRingioSyncMap; key: string; onSuccess?: (data: any) => void; onError?: (error: any) => void }) {
  if (typeof window === "undefined") {
    consola.warn("We can not fetch a map key data on the server")
    return
  }

  if (!window.ringioFrontV3.twilio.sync.client) {
    consola.warn("Sync client is not ready!")
    return
  }

  const syncClient = window.ringioFrontV3.twilio.sync.client!

  if (syncClient.connectionState !== "connected") {
    consola.warn("Sync client is not connected!")
    return
  }

  const selectedMap: SyncMap | undefined = window.ringioFrontV3.twilio.sync.initialization.maps[map] as SyncMap | undefined

  if (!selectedMap) {
    consola.warn(`Map ${map} is not loaded!`)
    return
  }

  try {
    const resultData = await selectedMap.get(key)

    // @ts-ignore
    const _item: Record<string, unknown> = resultData.data?.deflated ? inflateMapItem(resultData.data) : resultData?.data

    // Auto-store contact data when fetching from contacts map
    if (map === "contacts" && _item) {
      consola.log(`[fetchMapKeyData] Auto-storing contact data for key ${key}`)
      processAndStoreContactData(_item, "fetch")
    }

    // Still call the custom onSuccess callback if provided
    if (onSuccess) {
      onSuccess?.(_item)
    }
  } catch (err) {
    consola.warn(`Error fetching map key ${key} for the map ${map}`, err)
    onError?.(err)
  }
}
